// =====================================================
// AI REPORTING ENGINE - MOCK DATA
// UI-Only Implementation
// =====================================================

// =====================================================
// PHASE 1: REPORT TEMPLATES
// =====================================================

export const mockReportTemplates = [
  {
    id: '1',
    name: 'Mortgage Compliance Report',
    tone: 'Regulatory',
    mandatorySections: ['Executive Summary', 'Key Findings', 'Compliance Notes', 'Appendix'],
    allowedCharts: ['Bar', 'Table', 'KPI Cards'],
    isActive: true,
    createdAt: '2024-01-15T10:30:00Z'
  },
  {
    id: '2',
    name: 'Executive Summary Report',
    tone: 'Executive',
    mandatorySections: ['Executive Summary', 'Key Findings', 'Visual Evidence'],
    allowedCharts: ['Bar', 'Line', 'KPI Cards'],
    isActive: true,
    createdAt: '2024-02-20T14:45:00Z'
  },
  {
    id: '3',
    name: 'Operational Dashboard',
    tone: 'Operational',
    mandatorySections: ['Key Findings', 'Visual Evidence', 'Appendix'],
    allowedCharts: ['Bar', 'Line', 'Table'],
    isActive: false,
    createdAt: '2024-03-10T09:15:00Z'
  }
];

export const reportSections = [
  { id: 'executive_summary', name: 'Executive Summary', description: 'High-level overview for executives' },
  { id: 'key_findings', name: 'Key Findings', description: 'Main insights and discoveries from data analysis' },
  { id: 'visual_evidence', name: 'Visual Evidence', description: 'Charts, graphs, and visual representations' },
  { id: 'compliance_notes', name: 'Compliance Notes', description: 'Regulatory and compliance-related information' },
  { id: 'appendix', name: 'Appendix', description: 'Supporting data and additional details' }
];

export const chartTypes = [
  { id: 'bar', name: 'Bar', icon: '📊', description: 'Vertical or horizontal bar charts' },
  { id: 'line', name: 'Line', icon: '📈', description: 'Trend lines and time series' },
  { id: 'table', name: 'Table', icon: '📋', description: 'Tabular data display' },
  { id: 'kpi_cards', name: 'KPI Cards', icon: '🎯', description: 'Key performance indicator cards' }
];

export const reportTones = [
  { id: 'regulatory', name: 'Regulatory', description: 'Formal tone suitable for regulatory submissions' },
  { id: 'executive', name: 'Executive', description: 'Concise, business-focused tone for executives' },
  { id: 'operational', name: 'Operational', description: 'Detailed, technical tone for operational teams' }
];

// =====================================================
// PHASE 2: REPORT EXPORT
// =====================================================

export const mockExportMetadata = {
  generatedBy: 'John Smith',
  generationTime: new Date().toISOString(),
  assumptions: 'Data aggregated from Q1-Q4 2024. Excludes pending transactions. All values are in USD.',
  kpisUsed: ['Total Auction Value', 'Bid Success Rate', 'Average Property Price', 'Compliance Score', 'Investor Activity Index']
};

export const audienceRoles = [
  { id: 'executive', name: 'Executive', description: 'Executive level audience' },
  { id: 'compliance_officer', name: 'Compliance Officer', description: 'Compliance and regulatory audience' }
];

export const reportFormats = [
  { id: 'pdf', name: 'PDF', icon: '📄' },
  { id: 'excel', name: 'Excel', icon: '📊' }
];

// =====================================================
// PHASE 3: DISTRIBUTION RULES
// =====================================================

export const mockDistributionRules = [
  {
    id: '1',
    name: 'Executive Distribution',
    role: 'Executive',
    recipients: ['ceo@company.com', 'cfo@company.com', 'coo@company.com'],
    includedSections: ['Executive Summary', 'Key Findings'],
    isActive: true,
    createdAt: '2024-01-10T08:00:00Z'
  },
  {
    id: '2',
    name: 'Compliance Team',
    role: 'Compliance Officer',
    recipients: ['compliance@company.com', 'legal@company.com'],
    includedSections: ['Executive Summary', 'Key Findings', 'Compliance Notes', 'Appendix'],
    isActive: true,
    createdAt: '2024-01-12T10:30:00Z'
  },
  {
    id: '3',
    name: 'Full Report Distribution',
    role: 'Executive',
    recipients: ['board@company.com'],
    includedSections: ['Executive Summary', 'Key Findings', 'Visual Evidence', 'Compliance Notes', 'Appendix'],
    isActive: false,
    createdAt: '2024-02-05T14:00:00Z'
  }
];

export const distributionRoles = [
  { id: 'executive', name: 'Executive', description: 'Senior executives who receive summarized reports' },
  { id: 'compliance_officer', name: 'Compliance Officer', description: 'Compliance team members who receive detailed compliance reports' }
];

// =====================================================
// PHASE 4: SEMANTIC MODEL - TABLE MAPPING
// =====================================================

export const mockTables = [
  { id: '1', name: 'tbl_properties', status: 'mapped', entityType: 'Property', description: 'Contains all real estate property listings with details like address, size, and valuation.' },
  { id: '2', name: 'tbl_auctions', status: 'mapped', entityType: 'Auction', description: 'Stores auction events including start/end times, reserve prices, and status.' },
  { id: '3', name: 'tbl_bids', status: 'not_mapped', entityType: null, description: '' },
  { id: '4', name: 'tbl_users', status: 'not_mapped', entityType: null, description: '' },
  { id: '5', name: 'tbl_audit_logs', status: 'mapped', entityType: 'Audit Log', description: 'System audit trail capturing all user actions and system events.' },
  { id: '6', name: 'tbl_transactions', status: 'not_mapped', entityType: null, description: '' },
  { id: '7', name: 'tbl_compliance_records', status: 'not_mapped', entityType: null, description: '' },
  { id: '8', name: 'tbl_investor_profiles', status: 'not_mapped', entityType: null, description: '' }
];

export const businessEntityTypes = [
  { id: 'property', name: 'Property', description: 'Real estate property data' },
  { id: 'auction', name: 'Auction', description: 'Auction event data' },
  { id: 'bidding', name: 'Bidding', description: 'Bid transaction data' },
  { id: 'audit_log', name: 'Audit Log', description: 'System audit and activity logs' }
];

// =====================================================
// PHASE 5: SEMANTIC MODEL - COLUMN SEMANTICS
// =====================================================

export const mockColumns = [
  {
    id: '1',
    name: 'customer_ssn',
    semanticLabel: 'Social Security Number',
    dataType: 'String',
    unit: '',
    sensitivity: ['PII'],
    allowedUsage: ['Display'],
    tableName: 'tbl_users'
  },
  {
    id: '2',
    name: 'property_value',
    semanticLabel: 'Property Estimated Value',
    dataType: 'Decimal',
    unit: 'USD',
    sensitivity: ['Financial'],
    allowedUsage: ['Aggregation', 'Display', 'Filtering'],
    tableName: 'tbl_properties'
  },
  {
    id: '3',
    name: 'auction_date',
    semanticLabel: 'Auction Date',
    dataType: 'Date',
    unit: '',
    sensitivity: [],
    allowedUsage: ['Display', 'Filtering'],
    tableName: 'tbl_auctions'
  },
  {
    id: '4',
    name: 'compliance_flag',
    semanticLabel: 'Compliance Status',
    dataType: 'Boolean',
    unit: '',
    sensitivity: ['Compliance-Critical'],
    allowedUsage: ['Display', 'Filtering'],
    tableName: 'tbl_compliance_records'
  },
  {
    id: '5',
    name: 'bid_amount',
    semanticLabel: 'Bid Amount',
    dataType: 'Decimal',
    unit: 'USD',
    sensitivity: ['Financial'],
    allowedUsage: ['Aggregation', 'Display', 'Filtering'],
    tableName: 'tbl_bids'
  },
  {
    id: '6',
    name: 'investor_email',
    semanticLabel: 'Investor Email Address',
    dataType: 'String',
    unit: '',
    sensitivity: ['PII'],
    allowedUsage: ['Display'],
    tableName: 'tbl_investor_profiles'
  },
  {
    id: '7',
    name: 'property_sqft',
    semanticLabel: 'Property Size',
    dataType: 'Integer',
    unit: 'sqft',
    sensitivity: [],
    allowedUsage: ['Aggregation', 'Display', 'Filtering'],
    tableName: 'tbl_properties'
  },
  {
    id: '8',
    name: 'transaction_amount',
    semanticLabel: 'Transaction Amount',
    dataType: 'Decimal',
    unit: 'USD',
    sensitivity: ['Financial', 'Compliance-Critical'],
    allowedUsage: ['Aggregation', 'Display'],
    tableName: 'tbl_transactions'
  }
];

export const columnDataTypes = [
  { id: 'string', name: 'String', description: 'Text data type' },
  { id: 'integer', name: 'Integer', description: 'Whole number data type' },
  { id: 'decimal', name: 'Decimal', description: 'Floating point number' },
  { id: 'date', name: 'Date', description: 'Date without time' },
  { id: 'datetime', name: 'DateTime', description: 'Date with time' },
  { id: 'boolean', name: 'Boolean', description: 'True/False value' }
];

export const sensitivityTypes = [
  { id: 'pii', name: 'PII', description: 'Personally Identifiable Information', color: '#ef4444' },
  { id: 'financial', name: 'Financial', description: 'Financial sensitive data', color: '#f59e0b' },
  { id: 'compliance_critical', name: 'Compliance-Critical', description: 'Data critical for regulatory compliance', color: '#8b5cf6' }
];

export const usageTypes = [
  { id: 'aggregation', name: 'Aggregation', description: 'Column can be used in aggregation operations' },
  { id: 'display', name: 'Display', description: 'Column can be displayed in reports' },
  { id: 'filtering', name: 'Filtering', description: 'Column can be used for filtering data' }
];
